/**
 * Trust Copy Constants - Single Source of Truth
 *
 * All trust-first messaging must use these constants to prevent drift.
 * Conforms to Trust Copy Checklist requirements.
 *
 * @see _bmad-output/planning-artifacts/epics/trust-copy-checklist-single-source-of-truth.md
 */

/** Required statements for participant-facing content */
export const TRUST_COPY = {
  /** Core participation statement */
  PARTICIPATION_OPTIONAL: 'Participation is optional and consequence-free.',

  /** Behaviour tracking statement */
  NO_TRACKING: 'Your behaviour is not tracked.',

  /** Combined optional + no tracking statement */
  OPTIONAL_NO_TRACKING: 'Participation is optional and consequence-free. Your behaviour is not tracked.',

  /** Data minimization statement for authenticated state */
  BASIC_IDENTITY_ONLY: 'We use only basic identity data from your work account.',

  /** Signed-in reassurance */
  SIGNED_IN_REASSURANCE: 'You are signed in. We use only basic identity data from your work account.',

  /** General opt-out reassurance */
  CAN_LEAVE_ANYTIME: 'You can pause or leave at any time.',

  /** Ignoring matches is consequence-free */
  IGNORE_NO_CONSEQUENCE: 'Ignoring a match has no consequences.',

  /** Full reassurance statement */
  FULL_REASSURANCE: 'You can pause or leave at any time. Ignoring a match has no consequences. This is designed to be genuinely optional.',

  /** Transparency page intro */
  TRANSPARENCY_INTRO: 'Participation is optional and consequence-free. Here is exactly what happens with your data.',

  /** Manager visibility statement */
  NO_MANAGER_VISIBILITY: 'Your manager cannot see whether you have joined, whether you respond to matches, or anything else about your participation. Programme sponsors see only aggregate numbers (like total participants), never individual information.',

  /** Transparency link text */
  TRANSPARENCY_LINK: 'What we store (and do not store)',

  /** Join confirmation heading */
  JOIN_CONFIRMATION_HEADING: "You're in.",

  /** Join confirmation body */
  JOIN_CONFIRMATION_BODY: 'You may be matched with a colleague for an optional conversation. There\'s nothing else to do right now.',

  /** Join confirmation settings hint */
  JOIN_CONFIRMATION_SETTINGS: 'You can pause or leave at any time from Settings.',

  /** JoinCard body text with leave/pause emphasis */
  JOIN_CARD_BODY: 'One programme, monthly matches, optional participation. You can pause or leave at any time — no questions asked.',

  /** FAQ: Can I leave? (Transparency page) */
  FAQ_CAN_I_LEAVE: 'Yes. You can pause (skip a cycle) or leave entirely at any time. There are no consequences, no notifications sent to anyone, and your data is removed if you leave.',

  /** ParticipantHome heading */
  PARTICIPANT_HOME_HEADING: "You're part of Collabolatte.",

  /** ParticipantHome body text */
  PARTICIPANT_HOME_BODY: "You may be matched with a colleague in the next cycle. We'll let you know by email if that happens.",

  /** ParticipantHome nothing-to-do text */
  PARTICIPANT_HOME_NOTHING_TO_DO: "Nothing to do right now — we'll be in touch.",
} as const;

/** Data storage explanations for transparency page */
export const DATA_STORAGE = {
  STORED: {
    EMAIL: {
      primary: 'Your work email address',
      secondary: 'Used to send match notifications',
    },
    NAME: {
      primary: 'Your name (from your work account)',
      secondary: 'Shown to your match so they know who to expect',
    },
    STATUS: {
      primary: 'Your participation status',
      secondary: 'Whether you are active, paused, or have left',
    },
  },
  NOT_STORED: {
    RESPONSE_TRACKING: {
      primary: 'Whether you respond to matches',
      secondary: 'No behavioural tracking',
    },
    CONVERSATION_TRACKING: {
      primary: 'Whether conversations happen',
      secondary: 'No outcome measurement',
    },
    ACTIVITY_PATTERNS: {
      primary: 'Activity or usage patterns',
      secondary: 'No analytics',
    },
    PERSONAL_DETAILS: {
      primary: 'Personal details beyond name and email',
      secondary: 'No profiles, interests, or preferences',
    },
  },
} as const;
